import { FC, useMemo, useEffect } from 'react';
import { Switch, useRouteMatch } from 'react-router-dom';
import { AddonFeatureManager, AuthenticationManager } from 'core';
import { Permission } from 'core/auth/Permission';
import { hasFuncs } from 'core/permission/PermissionDSL';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import {
  DefaultL1ObjectManager,
  L1ObjectManager,
} from 'core/l1Object/L1ObjectManager';
import { ROUTE_PATH } from 'enum/RoutePath';
import { LoadingIndicator } from 'components/LoadingIndicator';
import RouteWithPermission from 'components/RouteWithPermission/RouteWithPermission';
import { RtbCampaigns } from 'containers/RtbCampaigns';
import { DefaultRtbCampaignsModel } from 'containers/RtbCampaigns/RtbCampaignsModel';
import { L1ObjectDetail } from 'containers/L1Objects/L1ObjectDetail/L1ObjectDetail';

import { useFetchOrderDetail } from './OrderDetailHooks';
import OrderDetailContent from '../OrderDetailContent/OrderDetailContent';

const l1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager();
const modifyPermissionItem = l1ObjectManager.getModifyPermissionItem(
  L1ObjectChannel.RTB,
);

const CampaignDetail = ({ order, fetchOrder, addonFeatureManager }) => {
  const campaignsModel = useMemo(
    () => new DefaultRtbCampaignsModel(order, addonFeatureManager),
    [order, addonFeatureManager],
  );
  useEffect(() => {
    if (!order) fetchOrder();
  }, [order, fetchOrder]);

  if (!order) return null;
  return <RtbCampaigns model={campaignsModel} />;
};

const OrderDetailWithCampaignList = ({
  order,
  fetchOrder,
  addonFeatureManager,
  authenticationManager,
}) => {
  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  if (!order) return null;
  return (
    <L1ObjectDetail order={order} addonFeatureManager={addonFeatureManager}>
      <OrderDetailContent
        order={order}
        refetchOrder={fetchOrder}
        authenticationManager={authenticationManager}
        addonFeatureManager={addonFeatureManager}
      />
    </L1ObjectDetail>
  );
};

interface OrderDetailProps {
  orderNumber: string;
  authenticationManager: AuthenticationManager;
  addonFeatureManager: AddonFeatureManager;
}

export const OrderDetail: FC<OrderDetailProps> = ({
  orderNumber,
  authenticationManager,
  addonFeatureManager,
}) => {
  const { url: currentUrl } = useRouteMatch();
  const { loading, order, fetchOrder } = useFetchOrderDetail(orderNumber);

  return (
    <>
      {loading && <LoadingIndicator />}
      <Switch>
        <RouteWithPermission
          permissionAware={hasFuncs(Permission.CAMPAIGN_READ).or(
            modifyPermissionItem,
          )}
          redirectPath={`${currentUrl}/campaigns/${ROUTE_PATH.ERROR403}`}
          path={`${currentUrl}/campaigns`}
        >
          <CampaignDetail
            order={order}
            fetchOrder={fetchOrder}
            addonFeatureManager={addonFeatureManager}
          />
        </RouteWithPermission>
        <RouteWithPermission
          permissionAware={hasFuncs(Permission.ORDER_READ).and(
            hasFuncs(Permission.CAMPAIGN_READ),
          )}
          redirectPath={`${currentUrl}/${ROUTE_PATH.ERROR403}`}
        >
          <OrderDetailWithCampaignList
            order={order}
            fetchOrder={fetchOrder}
            addonFeatureManager={addonFeatureManager}
            authenticationManager={authenticationManager}
          />
        </RouteWithPermission>
      </Switch>
    </>
  );
};
